import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "/images/logo-primary.png";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Industries", href: "#industries" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-xl bg-white/80 border-b border-slate-200 shadow-sm ${scrolled
          ? "md:backdrop-blur-xl md:bg-white/80 md:border-b md:border-slate-200 md:shadow-sm"
          : "md:backdrop-blur-none md:bg-transparent md:border-b-0 md:shadow-none"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#home" data-testid="nav-logo" className="flex items-center" onClick={(e) => handleNavClick(e, "#home")}>
            <img
              src={LOGO_URL}
              alt="DigitalBlue Services"
              className="h-10 md:h-14 logo-blend w-auto"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase()}-link`}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-[#0047AB] transition-colors tracking-wide"
              >
                {link.label}
              </a>
            ))}
            <Button
              data-testid="nav-cta-button"
              className="ml-4 bg-[#0047AB] hover:bg-[#003585] text-white rounded-md px-6 text-sm font-medium transition-all hover:scale-[0.98]"
              onClick={(e) => handleNavClick(e, "#contact")}
            >
              Get in Touch
            </Button>
          </nav>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-slate-700"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <nav data-testid="mobile-menu" className="md:hidden pb-6 border-t border-slate-200 mt-2 pt-4">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}-link`}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="px-4 py-3 text-sm font-medium text-slate-700 hover:text-[#0047AB] hover:bg-slate-50 rounded-md transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <Button
                data-testid="mobile-nav-cta-button"
                className="mt-2 bg-[#0047AB] hover:bg-[#003585] text-white rounded-md"
                onClick={(e) => handleNavClick(e, "#contact")}
              >
                Get in Touch
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
